// Content registry: cars and stages are discovered from their folders at build time.
// Adding one is a new folder (car.json / stage.json + assets) — no engine edits.
import type { CarSpec } from '../sim/types';
import type { StageSpec } from '../world/types';

const carJson = import.meta.glob('./cars/*/car.json', { eager: true, import: 'default' }) as Record<string, CarSpec>;
const stageJson = import.meta.glob('./stages/*/stage.json', { eager: true, import: 'default' }) as Record<string, StageSpec>;
const carAssets = import.meta.glob('./cars/*/*.{glb,webp,png}', { eager: true, import: 'default', query: '?url' }) as Record<string, string>;
const stageNotes = import.meta.glob('./stages/*/notes.generated.json', { eager: true, import: 'default' }) as Record<string, unknown>;

/** Resolve '..' segments so a car can reuse another car's model (e.g. '../kite-r2/model.glb'). */
function normalise(p: string): string {
  const out: string[] = [];
  for (const seg of p.split('/')) {
    if (seg === '..') out.pop();
    else out.push(seg);
  }
  return out.join('/');
}

function folderOf(path: string): string {
  const parts = path.split('/');
  return parts[parts.length - 2];
}

export interface CarEntry {
  id: string;
  folder: string;
  spec: CarSpec;
  modelUrl: string | null;
}

export interface StageEntry {
  id: string;
  folder: string;
  spec: StageSpec;
  notes: unknown | null;
}

export const CARS: CarEntry[] = Object.entries(carJson)
  .map(([path, spec]) => {
    const folder = folderOf(path);
    const modelPath = spec.model ? normalise(`./cars/${folder}/${spec.model}`) : null;
    return { id: spec.id, folder, spec, modelUrl: modelPath ? (carAssets[modelPath] ?? null) : null };
  })
  .sort((a, b) => a.id.localeCompare(b.id));

export const STAGES: StageEntry[] = Object.entries(stageJson)
  .map(([path, spec]) => {
    const folder = folderOf(path);
    return { id: spec.id, folder, spec, notes: stageNotes[`./stages/${folder}/notes.generated.json`] ?? null };
  })
  .sort((a, b) => a.id.localeCompare(b.id));

export const visibleCars = (): CarEntry[] => CARS.filter((c) => !c.spec.hidden);
export const visibleStages = (): StageEntry[] => STAGES.filter((s) => !s.spec.hidden);

export function getCar(id: string): CarEntry {
  const c = CARS.find((x) => x.id === id);
  if (!c) throw new Error(`Unknown car "${id}"`);
  return c;
}

export function getStage(id: string): StageEntry {
  const s = STAGES.find((x) => x.id === id);
  if (!s) throw new Error(`Unknown stage "${id}"`);
  return s;
}
